/**
 * Real-Time Counter-Signal Detection (Phase 19).
 *
 * Bridge between the pure `detectInTradeCounterSignals` (which runs over
 * a closed trade's bars at backfill time) and the LiveTradePanel's
 * `computeLiveInsights` pipeline (which renders coaching whispers on
 * each tick while the trade is open).
 *
 * Architecture:
 *   1. LiveTradePanel polls `barDataService.fetchBars` every ~60s for
 *      the open trade's symbol on 5m + 15m timeframes (cached, cheap)
 *   2. Bars are passed into `computeLiveInsights` via the new
 *      `htfBars` ctx field
 *   3. `computeLiveInsights` calls `buildRealtimeCounterSignals` here
 *   4. This module runs the detector on the live bars + the open trade,
 *      then formats any detected signals as the signal shape that
 *      LiveTradeInsights renders (key, tone, tier, label, detail,
 *      priority)
 *
 * Why "real-time" is the right framing:
 *   The detector itself is the same as backfill — but bars in the live
 *   case INCLUDE the in-progress bar (which we exclude — only firing on
 *   bar-close). So as bars close one by one, the detector progressively
 *   detects more signals. The polling + the detector's dedup
 *   (first-cross-per-key) combine to make this idempotent.
 *
 * Priority: 88 — higher than the history-based "Watch X" whisper
 * (priority 78) because a real-time fire is more actionable than a
 * priming prediction. When both fire on the same key, the real-time
 * one wins the top slot.
 */

import { detectInTradeCounterSignals, signalLabel, signalSignature } from '../barAnalysis/counterSignals.js';

// Real-time signal priority — outranks the history-based priming whisper
// so when a signal ACTUALLY fires, it surfaces above the "watch for X"
// warning of the same signature.
const REALTIME_PRIORITY = 88;

const fmtMins = (secs) => {
  if (!Number.isFinite(secs) || secs < 0) return '';
  const m = Math.round(secs / 60);
  if (m < 1) return '<1m';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r > 0 ? `${h}h${r}m` : `${h}h`;
};

/**
 * Build a single signal-shaped object from a detector event. The shape
 * matches the contract LiveTradeInsights expects:
 *   { key, tone, tier, label, detail, priority }
 */
const formatAsSignal = (event) => {
  const sig = signalSignature(event);
  const label = signalLabel(event);
  const timing = fmtMins(event.secondsIntoTrade);
  const distanceText = Number.isFinite(event.distancePoints)
    ? ` (${event.distancePoints}pts${event.distanceR != null ? `, ${event.distanceR}R` : ''})`
    : '';
  return {
    // Prefix with 'rt:' so the dedup logic in computeLiveInsights can
    // distinguish real-time fires from history-based whispers
    key: `rt-counter:${sig}`,
    tone: 'warn',
    tier: 'free',
    label: `⚠ ${label}`,
    // Counter-signal copy is a WARNING, not a confirmation. Pre-fix the
    // detail read "the signal you should be watching for just happened",
    // which sounded like good news (especially for shorts where the
    // entry-bar HIGH being broken is adverse motion toward the stop).
    // Now we name it explicitly: structure failed against the trade,
    // here's how much, here's when.
    detail: `Counter-signal fired ${timing} in${distanceText} — structure broke against your trade. Watch for confirmation or exit.`,
    priority: REALTIME_PRIORITY,
  };
};

/**
 * Build real-time counter-signal whispers for an open trade.
 *
 * @param {Object} trade — must be open + have symbol, date, entryTime, direction
 * @param {Object} htfBars — { '5m': bars[], '15m': bars[] } from the polling fetch
 * @returns {Array} signal-shaped events for LiveTradeInsights
 */
export const buildRealtimeCounterSignals = (trade, htfBars) => {
  if (!trade?.isOpen) return [];
  if (!htfBars || typeof htfBars !== 'object') return [];
  if (Object.keys(htfBars).length === 0) return [];

  const events = detectInTradeCounterSignals(trade, { htfBars });
  if (events.length === 0) return [];

  // Dedupe by signature — the detector already does this within a single
  // call, but defensive in case the calling layer ever merges multiple
  // detections. Same-signature events are the same logical signal.
  const seenSigs = new Set();
  const out = [];
  for (const ev of events) {
    const sig = signalSignature(ev);
    if (seenSigs.has(sig)) continue;
    seenSigs.add(sig);
    out.push(formatAsSignal(ev));
  }
  return out;
};

// Export priority constant so the existing live-insights ranking system
// can reason about precedence if needed
export { REALTIME_PRIORITY };
